import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, type AuthenticationResult } from '@azure/msal-browser';
import { useEffect, useState, type ReactNode } from 'react';
import { msalConfig } from './config';
import { resumeInviteAfterRedirect } from './pendingInvite';

/** Where the user was headed before being bounced to sign-in. */
const RETURN_TO_KEY = 'mlp.returnTo';

export function rememberReturnTo(path: string) {
  sessionStorage.setItem(RETURN_TO_KEY, path);
}

export function takeReturnTo(): string | null {
  const value = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return value;
}

export const pca = new PublicClientApplication(msalConfig);

/**
 * Initialises MSAL and processes the redirect response before rendering the
 * app. Rendering children first would let route guards read a signed-out state
 * during the split second before the redirect is handled, causing a redirect
 * loop.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await pca.initialize();

      pca.addEventCallback((message) => {
        if (message.eventType === EventType.LOGIN_SUCCESS && message.payload) {
          const { account } = message.payload as AuthenticationResult;
          if (account) {
            pca.setActiveAccount(account);
          }
        }
      });

      // ⚠️ Caught (M2-20): a sign-in that comes back as an error — the person
      // cancelled at Entra, or abandoned "Forgot password?" — rejects here, and
      // uncaught it left `ready` false: a blank page with no way on. It is now
      // treated as "not signed in", which every guard already handles.
      let response: AuthenticationResult | null = null;
      let redirectFailed = false;
      try {
        response = await pca.handleRedirectPromise();
      } catch (error) {
        redirectFailed = true;
        // The code only: MSAL's message can carry the account's name.
        console.error('[msal] sign-in did not complete', (error as { errorCode?: string })?.errorCode);
      }
      // Before the router mounts: an invitee coming back from sign-in returns
      // to the invite page, not the dashboard (see pendingInvite.ts).
      resumeInviteAfterRedirect(response, redirectFailed);
      if (response?.account) {
        pca.setActiveAccount(response.account);
      } else if (!pca.getActiveAccount()) {
        const [first] = pca.getAllAccounts();
        if (first) {
          pca.setActiveAccount(first);
        }
      }

      if (!cancelled) {
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return null;
  }

  return <MsalProvider instance={pca}>{children}</MsalProvider>;
}
